import type {
  StepTooltip,
  TCPFrame,
  TCPOffsetOption,
  MotionChildTCPOption,
} from '@sb/motion-planning';
import {
  DEFAULT_TCP_OFFSET_OPTION,
  isSymbolicTooltip,
  resolveStepTooltip,
} from '@sb/motion-planning';

/**
 * The frame a resolved target carries: the tooltip the pose was taught in,
 * plus the equipment instance that defined it. Both halves are absent on
 * targets stored before the fields existed.
 */
export interface SavedPoseFrame {
  tcpOption?: TCPOffsetOption;
  gripperID?: string;
}

/**
 * The frame a resolved target was taught in, read off the target itself. Both
 * callers build the resolver's input this way, so neither can shape it
 * differently.
 */
export function savedPoseFrame(target: {
  tcpOption?: TCPOffsetOption;
  gripperID?: string;
}): SavedPoseFrame {
  return { tcpOption: target.tcpOption, gripperID: target.gripperID };
}

export interface TooltipModeInput {
  /** The child's own stored selection. */
  tcpOption: MotionChildTCPOption;
  /** The frame the target pose was taught in, once the target has resolved. */
  savedFrame: SavedPoseFrame;
  /** The robot's Active Tooltip, captured at the enclosing move's start. */
  activeTooltip: TCPFrame | undefined;
  /** The enclosing Move step's tooltip, what `'parent'` resolves through. */
  parentTooltip: StepTooltip | undefined;
}

export interface TooltipResolutionInput extends TooltipModeInput {
  /** The instance to pair with an outright-named `tcpOption`. */
  gripperID: string | undefined;
  /**
   * A frame the parent pins: a Weld step's torch, or a constant-speed Move
   * step's tooltip. The child cannot override it. A name-only frame (a Weld
   * torch, a legacy constant-speed tooltip) leaves the saved pose's own
   * `gripperID` in place; a frame that names its equipment outright replaces
   * it — pairing the parent's chosen device with the device the pose was
   * taught on names neither.
   */
  parentForcedTooltip: TCPFrame | undefined;
  /**
   * The single frame a relative-to-current waypoint both reads the carried
   * pose in and commands its target in. Arcs never have one.
   */
  readWriteFrame: TCPFrame | undefined;
}

/**
 * What to write onto the arm target: the option to command in, and the
 * equipment instance to pair with it. Callers stamp both halves.
 *
 * Both halves always come from one frame. Taking the option from one and the
 * instance from another names a tooltip neither frame describes: an `ee-*`
 * option is under-determined without the row that publishes it, so pairing a
 * name with some other row's id resolves to that row's tip, not to the tip
 * either frame meant. The one deliberate mix is the parent pin, which is a
 * name-only pin on purpose — see `parentForcedTooltip`.
 */
export interface ResolvedTooltip {
  effectiveTCPOption: TCPOffsetOption;
  gripperID: string | undefined;
}

/**
 * The frame a symbolic `tcpOption` names, or `undefined` when it names nothing
 * concrete — `'auto'` on a pose that stored no tooltip of its own. Callers read
 * `undefined` as the wrist flange rather than throwing.
 *
 * A `tcpOption` naming a tooltip outright is not symbolic and answers
 * `undefined` too: it names itself, and the instance to pair it with lives
 * beside it on the child rather than in any frame this reads.
 *
 * `'active'` and `'parent'` name nothing when no Active Tooltip reaches them: a
 * caller that passes none at all, or a Move step on a robot whose state holds no
 * tooltip and whose `setActiveTooltip` flag is off.
 */
export function resolveTooltipModeFrame({
  tcpOption,
  savedFrame,
  activeTooltip,
  parentTooltip,
}: TooltipModeInput): TCPFrame | undefined {
  switch (tcpOption) {
    case 'auto':
      return savedFrame.tcpOption
        ? { tcpOption: savedFrame.tcpOption, gripperID: savedFrame.gripperID }
        : undefined;
    case 'active':
      return activeTooltip;
    case 'parent':
      return resolveStepTooltip(parentTooltip, activeTooltip);
    default:
      // A frame named outright is not symbolic: it names itself, and the
      // instance to pair it with lives beside it on the child.
      return undefined;
  }
}

/**
 * The tooltip a motion child — a waypoint or an arc — commands its target in.
 * Both step kinds resolve through this one function, so a Move step holding one
 * of each cannot resolve them differently.
 *
 * The precedence, highest first:
 *
 *  1. `parentForcedTooltip`, the frame the parent pins — a Weld step's torch,
 *     or a constant-speed Move step's tooltip. The child cannot override it.
 *  2. `tcpOption` naming one of the symbolic members, which resolve through
 *     `resolveTooltipModeFrame`: the taught frame for `'auto'`, the Active
 *     Tooltip for `'active'`, the enclosing Move step's for `'parent'`.
 *  3. `tcpOption` naming a tooltip outright, paired with the child's own
 *     `gripperID`.
 *
 * A symbolic member that names nothing concrete names the wrist flange — the
 * same thing "no tooltip" means everywhere else, including what the reconciler
 * reverts an unsupplied Active Tooltip to. `'active'` and `'parent'` do NOT
 * inherit the saved pose's tooltip: that frame belongs to a selection they did
 * not make.
 *
 * A relative-to-current waypoint is the one exception, and it is about motion
 * rather than naming: it commands in the very frame it read its carried pose
 * in, so answering the flange there would displace the target by the tooltip
 * offset. Read and write frames stay equal whatever the member failed to name.
 *
 * The shape that reaches the flange is `'auto'` on a pose that stored no
 * tooltip, which the editor blocks — so it is the floor under a routine that
 * arrived some other way, not a resolution to author against.
 */
export function resolveMotionChildTooltip({
  tcpOption,
  gripperID,
  savedFrame,
  parentForcedTooltip,
  readWriteFrame,
  activeTooltip,
  parentTooltip,
}: TooltipResolutionInput): ResolvedTooltip {
  if (parentForcedTooltip) {
    // A name-only pin (a Weld torch, a legacy constant-speed tooltip) names
    // an option, not an instance, so the instance the pose was taught with
    // stands. A pin that names its equipment outright replaces it: pairing
    // the parent's chosen device with the pose's teach device names neither.
    return {
      effectiveTCPOption: parentForcedTooltip.tcpOption,
      gripperID: parentForcedTooltip.gripperID ?? savedFrame.gripperID,
    };
  }

  if (isSymbolicTooltip(tcpOption)) {
    const selectionFrame = resolveTooltipModeFrame({
      tcpOption,
      savedFrame,
      activeTooltip,
      parentTooltip,
    });

    // Whole frames, never halves: a selection that named nothing falls through
    // to the read frame with its instance attached. Taking the option from one
    // frame and the instance from another names a tooltip neither describes.
    //
    // Commanding the read frame keeps the read and the command matched: a
    // relative-to-current waypoint that answered the flange here would displace
    // its target by the tooltip offset.
    const commandFrame = selectionFrame ?? readWriteFrame;

    return {
      effectiveTCPOption: commandFrame?.tcpOption ?? DEFAULT_TCP_OFFSET_OPTION,
      gripperID: commandFrame?.gripperID,
    };
  }

  return { effectiveTCPOption: tcpOption, gripperID };
}
